import React, { useState, useEffect, useMemo } from "react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
    FileSpreadsheet,
    Download,
    Printer,
    Calendar,
    Filter,
    Clock,
    User,
    Car,
    Bike,
    Sparkles,
    Baby,
    Coffee,
    Newspaper,
    Utensils,
    Shirt,
    Wrench,
    UserCheck,
    X,
    RefreshCw,
    Search,
    ShieldCheck
} from "lucide-react";
import { getVisitorPhotoSrc } from "./VisitorPhotoModal";

const SERVICE_ICONS = {
    Babysitter: Baby,
    Maid: Sparkles,
    Milkman: Coffee,
    Newspaper: Newspaper,
    Cook: Utensils,
    Driver: Car,
    "Car Cleaner": Car,
    Laundry: Shirt,
    "Society Staff": Wrench,
    Other: UserCheck,
};

function formatDate(isoStr) {
    if (!isoStr) return "—";
    try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return isoStr;
        return d.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    } catch {
        return isoStr;
    }
}

function formatTime(isoStr) {
    if (!isoStr) return "—";
    try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
        return "—";
    }
}

export default function DailyStaffAttendanceReportModal({
    open,
    onClose,
    helpers = [],
    initialHelperId = null,
    onViewPhoto,
}) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const todayStr = new Date().toISOString().split("T")[0];

    // Filters
    const [datePreset, setDatePreset] = useState("month"); // "today" | "yesterday" | "7days" | "month" | "all" | "custom"
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(1); // 1st of current month
        return d.toISOString().split("T")[0];
    });
    const [endDate, setEndDate] = useState(todayStr);
    const [selectedHelperId, setSelectedHelperId] = useState(initialHelperId || "all");
    const [selectedService, setSelectedService] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState("all"); // "all" | "inside" | "exited"
    const [searchQuery, setSearchQuery] = useState("");

    // State
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloadingCsv, setDownloadingCsv] = useState(false);

    // Sync initialHelperId if passed
    useEffect(() => {
        if (initialHelperId) {
            setSelectedHelperId(initialHelperId);
        }
    }, [initialHelperId]);

    // Handle preset changes
    const applyDatePreset = (preset) => {
        setDatePreset(preset);
        const now = new Date();
        const today = now.toISOString().split("T")[0];

        if (preset === "today") {
            setStartDate(today);
            setEndDate(today);
        } else if (preset === "yesterday") {
            const y = new Date();
            y.setDate(y.getDate() - 1);
            const yStr = y.toISOString().split("T")[0];
            setStartDate(yStr);
            setEndDate(yStr);
        } else if (preset === "7days") {
            const d = new Date();
            d.setDate(d.getDate() - 6);
            setStartDate(d.toISOString().split("T")[0]);
            setEndDate(today);
        } else if (preset === "month") {
            const d = new Date(now.getFullYear(), now.getMonth(), 1);
            setStartDate(d.toISOString().split("T")[0]);
            setEndDate(today);
        } else if (preset === "all") {
            setStartDate("");
            setEndDate("");
        }
    };

    // Fetch attendance logs
    const fetchReport = async () => {
        if (!open || !isAdmin) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (startDate) params.set("start_date", startDate);
            if (endDate) params.set("end_date", endDate);
            if (selectedHelperId && selectedHelperId !== "all") params.set("help_id", selectedHelperId);
            if (selectedService && selectedService !== "all") params.set("service_type", selectedService);
            if (selectedStatus && selectedStatus !== "all") params.set("status", selectedStatus);

            const r = await api.get(`/daily-help/logs?${params.toString()}`);
            setLogs(r.data || []);
        } catch (err) {
            console.error("Failed to fetch attendance logs:", err);
            toast.error("Could not load attendance logs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, startDate, endDate, selectedHelperId, selectedService, selectedStatus]);

    // Client-side text search on fetched logs
    const filteredLogs = useMemo(() => {
        if (!searchQuery.trim()) return logs;
        const q = searchQuery.toLowerCase().trim();
        return logs.filter((l) => {
            const flats = Array.isArray(l.flats) ? l.flats.join(" ") : String(l.flats || "");
            return (
                (l.name && l.name.toLowerCase().includes(q)) ||
                (l.pass_code && l.pass_code.toLowerCase().includes(q)) ||
                (l.service_type && l.service_type.toLowerCase().includes(q)) ||
                (l.phone && l.phone.includes(q)) ||
                (l.vehicle_no && l.vehicle_no.toLowerCase().includes(q)) ||
                flats.toLowerCase().includes(q)
            );
        });
    }, [logs, searchQuery]);

    // Metrics
    const metrics = useMemo(() => {
        const totalVisits = filteredLogs.length;
        const currentlyInside = filteredLogs.filter((l) => !l.exited_at).length;
        const uniqueStaff = new Set(filteredLogs.map((l) => l.help_id || l.name)).size;
        const totalMins = filteredLogs.reduce((acc, l) => acc + (l.duration_mins || 0), 0);
        const hrs = Math.floor(totalMins / 60);
        const mins = totalMins % 60;
        const totalDurationStr = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;

        return {
            totalVisits,
            currentlyInside,
            uniqueStaff,
            totalDurationStr,
        };
    }, [filteredLogs]);

    // Export directly to CSV (instant in-browser download)
    const exportToCSV = () => {
        if (filteredLogs.length === 0) {
            toast.error("No records to export for this filter range");
            return;
        }

        const headers = [
            "Staff Name",
            "Pass Code",
            "Service Role",
            "Phone",
            "Flats Served",
            "Vehicle No",
            "Date",
            "Entry Time (IN)",
            "Exit Time (OUT)",
            "Duration",
            "Status",
            "Verified By Guard",
        ];

        const rows = filteredLogs.map((l) => [
            `"${(l.name || "").replace(/"/g, '""')}"`,
            `"${l.pass_code || ""}"`,
            `"${l.service_type || ""}"`,
            `"${l.phone || ""}"`,
            `"${(Array.isArray(l.flats) ? l.flats.join(", ") : l.flats || "Society").replace(/"/g, '""')}"`,
            `"${l.vehicle_no || "None"}"`,
            `"${l.date || formatDate(l.entered_at)}"`,
            `"${formatTime(l.entered_at)}"`,
            `"${l.exited_at ? formatTime(l.exited_at) : "Currently Inside"}"`,
            `"${l.duration_str || ""}"`,
            `"${l.exited_at ? "Exited" : "Inside"}"`,
            `"${l.guard_name || "Gate Security"}"`,
        ]);

        const csvContent = "\uFEFF" + [headers.join(","), ...rows.map((e) => e.join(","))].join("\r\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const filename = `Daily_Staff_Attendance_${startDate || "All"}_to_${endDate || "Today"}.csv`;
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${filteredLogs.length} attendance records to CSV`);
    };

    // Print summary
    const handlePrint = () => {
        window.print();
    };

    if (!open || !isAdmin) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-2 sm:p-4 overflow-y-auto animate-in fade-in duration-200"
            onClick={onClose}
            data-testid="attendance-report-modal"
        >
            <div
                className="bg-white rounded-3xl border border-nest-line w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col my-auto max-h-[94vh]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-5 sm:p-6 border-b border-nest-line flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-nest-alt/30">
                    <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-2xl bg-nest-clay/15 text-nest-clay grid place-items-center flex-shrink-0">
                            <FileSpreadsheet size={22} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-nest-clay">
                                    Society Security & Tracking
                                </span>
                                {metrics.currentlyInside > 0 && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                        {metrics.currentlyInside} Inside Now
                                    </span>
                                )}
                            </div>
                            <h2 className="font-display text-2xl font-bold tracking-tight text-nest-ink">
                                Daily Staff Attendance Report
                            </h2>
                            <p className="text-xs text-nest-ink3 mt-0.5">
                                Track check-in/out timestamps, hours worked, and presence for household staff.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={exportToCSV}
                            disabled={filteredLogs.length === 0}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-xl bg-nest-clay hover:bg-nest-clayDark text-white shadow-2xs transition active:scale-95 disabled:opacity-50 cursor-pointer"
                            title="Export filtered records to Excel / CSV"
                            data-testid="export-csv-btn"
                        >
                            <Download size={14} />
                            <span>Export CSV</span>
                        </button>
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2.5 rounded-xl bg-white border border-nest-line hover:bg-nest-alt text-nest-ink shadow-2xs transition cursor-pointer"
                            title="Print attendance report"
                        >
                            <Printer size={14} />
                            <span className="hidden sm:inline">Print / PDF</span>
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-9 w-9 rounded-full grid place-items-center hover:bg-nest-alt text-nest-ink3 hover:text-nest-ink transition cursor-pointer"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Filter Controls Bar */}
                <div className="p-4 sm:p-5 border-b border-nest-line bg-white space-y-3">
                    {/* Date Presets */}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs font-medium">
                            <span className="text-nest-ink3 mr-1 flex items-center gap-1">
                                <Calendar size={13} /> Period:
                            </span>
                            {[
                                { id: "today", label: "Today" },
                                { id: "yesterday", label: "Yesterday" },
                                { id: "7days", label: "Last 7 Days" },
                                { id: "month", label: "This Month" },
                                { id: "all", label: "All Time" },
                            ].map((preset) => (
                                <button
                                    key={preset.id}
                                    type="button"
                                    onClick={() => applyDatePreset(preset.id)}
                                    className={`px-3 py-1.5 rounded-lg transition cursor-pointer whitespace-nowrap ${
                                        datePreset === preset.id
                                            ? "bg-nest-ink text-white font-semibold"
                                            : "bg-nest-alt hover:bg-nest-line/70 text-nest-ink2"
                                    }`}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>

                        {/* Custom Date Pickers */}
                        <div className="flex items-center gap-2 text-xs">
                            <div className="flex items-center gap-1 bg-nest-alt px-2.5 py-1.5 rounded-xl border border-nest-line">
                                <span className="text-nest-ink3 text-[11px]">From:</span>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.target.value);
                                        setDatePreset("custom");
                                    }}
                                    className="bg-transparent text-xs text-nest-ink font-medium outline-none cursor-pointer"
                                />
                            </div>
                            <span className="text-nest-ink3">to</span>
                            <div className="flex items-center gap-1 bg-nest-alt px-2.5 py-1.5 rounded-xl border border-nest-line">
                                <span className="text-nest-ink3 text-[11px]">To:</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => {
                                        setEndDate(e.target.value);
                                        setDatePreset("custom");
                                    }}
                                    className="bg-transparent text-xs text-nest-ink font-medium outline-none cursor-pointer"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={fetchReport}
                                disabled={loading}
                                className="h-8 w-8 rounded-lg bg-nest-alt hover:bg-nest-line text-nest-ink grid place-items-center transition cursor-pointer"
                                title="Refresh data"
                            >
                                <RefreshCw size={13} className={loading ? "animate-spin text-nest-clay" : ""} />
                            </button>
                        </div>
                    </div>

                    {/* Secondary Filters */}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5 pt-1 text-xs">
                        {/* Search Input */}
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nest-ink3" />
                            <input
                                type="text"
                                placeholder="Search by name, flat, phone…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-nest-alt/60 border border-nest-line rounded-xl pl-8 pr-3 py-2 text-xs focus:bg-white focus:border-nest-clay outline-none transition"
                            />
                        </div>

                        {/* Staff Member Selector */}
                        <div>
                            <select
                                value={selectedHelperId}
                                onChange={(e) => setSelectedHelperId(e.target.value)}
                                className="w-full bg-nest-alt/60 border border-nest-line rounded-xl px-3 py-2 text-xs focus:bg-white focus:border-nest-clay outline-none transition cursor-pointer font-medium"
                            >
                                <option value="all">All Staff Members ({helpers.length})</option>
                                {helpers.map((h) => (
                                    <option key={h.help_id} value={h.help_id}>
                                        {h.name} ({h.service_type})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Service Type Filter */}
                        <div>
                            <select
                                value={selectedService}
                                onChange={(e) => setSelectedService(e.target.value)}
                                className="w-full bg-nest-alt/60 border border-nest-line rounded-xl px-3 py-2 text-xs focus:bg-white focus:border-nest-clay outline-none transition cursor-pointer font-medium"
                            >
                                <option value="all">All Service Roles</option>
                                <option value="Babysitter">Babysitter / Nanny</option>
                                <option value="Maid">Maid / Housekeeping</option>
                                <option value="Cook">Cook / Chef</option>
                                <option value="Driver">Driver</option>
                                <option value="Milkman">Milkman</option>
                                <option value="Newspaper">Newspaper Delivery</option>
                                <option value="Car Cleaner">Car Cleaner</option>
                                <option value="Laundry">Laundry / Ironing</option>
                                <option value="Society Staff">Society Staff</option>
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="w-full bg-nest-alt/60 border border-nest-line rounded-xl px-3 py-2 text-xs focus:bg-white focus:border-nest-clay outline-none transition cursor-pointer font-medium"
                            >
                                <option value="all">All Attendance Status</option>
                                <option value="inside">Currently Inside Only</option>
                                <option value="exited">Completed Visits (Exited)</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* KPI Summary Cards */}
                <div className="p-4 sm:p-5 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-nest-alt/20 border-b border-nest-line">
                    <div className="bg-white p-3.5 rounded-2xl border border-nest-line">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-nest-ink3 block">
                            Total Visits / Logs
                        </span>
                        <div className="text-2xl font-display font-bold text-nest-ink mt-0.5">
                            {metrics.totalVisits}
                        </div>
                        <span className="text-[11px] text-nest-ink3">across selected period</span>
                    </div>

                    <div className="bg-white p-3.5 rounded-2xl border border-nest-line">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 block">
                            Currently Inside
                        </span>
                        <div className="text-2xl font-display font-bold text-emerald-700 mt-0.5 flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                            <span>{metrics.currentlyInside}</span>
                        </div>
                        <span className="text-[11px] text-nest-ink3">active gate passes</span>
                    </div>

                    <div className="bg-white p-3.5 rounded-2xl border border-nest-line">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-nest-ink3 block">
                            Unique Staff
                        </span>
                        <div className="text-2xl font-display font-bold text-nest-ink mt-0.5">
                            {metrics.uniqueStaff}
                        </div>
                        <span className="text-[11px] text-nest-ink3">helpers recorded</span>
                    </div>

                    <div className="bg-white p-3.5 rounded-2xl border border-nest-line">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-nest-clay block">
                            Total Time Tracked
                        </span>
                        <div className="text-2xl font-display font-bold text-nest-clay mt-0.5">
                            {metrics.totalDurationStr}
                        </div>
                        <span className="text-[11px] text-nest-ink3">cumulative presence</span>
                    </div>
                </div>

                {/* Attendance Table Area */}
                <div className="flex-1 overflow-y-auto min-h-[280px]">
                    {loading ? (
                        <div className="py-20 text-center text-xs text-nest-ink3 flex flex-col items-center justify-center gap-2.5">
                            <div className="h-6 w-6 rounded-full border-2 border-nest-clay border-t-transparent animate-spin" />
                            <span>Loading attendance history…</span>
                        </div>
                    ) : filteredLogs.length === 0 ? (
                        <div className="py-20 text-center text-nest-ink3 space-y-1">
                            <p className="font-semibold text-sm text-nest-ink2">No attendance records found.</p>
                            <p className="text-xs">
                                Try expanding the date range or clearing the staff / role filters.
                            </p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse text-xs" data-testid="attendance-table">
                            <thead>
                                <tr className="border-b border-nest-line bg-nest-alt/40 text-[11px] text-nest-ink3 uppercase tracking-wider sticky top-0 z-10 backdrop-blur-xs font-semibold">
                                    <th className="py-3 px-4">Staff Member</th>
                                    <th className="py-3 px-3">Role</th>
                                    <th className="py-3 px-3">Flats Served</th>
                                    <th className="py-3 px-3">Date</th>
                                    <th className="py-3 px-3">Entry (IN)</th>
                                    <th className="py-3 px-3">Exit (OUT)</th>
                                    <th className="py-3 px-3">Duration</th>
                                    <th className="py-3 px-3">Vehicle</th>
                                    <th className="py-3 px-4 text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-nest-line/70">
                                {filteredLogs.map((log) => {
                                    const isInside = !log.exited_at;
                                    const RoleIcon = SERVICE_ICONS[log.service_type] || UserCheck;
                                    return (
                                        <tr
                                            key={log.log_id}
                                            className="hover:bg-nest-alt/30 transition group"
                                            data-testid={`attendance-row-${log.log_id}`}
                                        >
                                            {/* Staff Member */}
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2.5">
                                                    {log.has_photo ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => onViewPhoto && onViewPhoto(log)}
                                                            className="h-8 w-8 rounded-xl overflow-hidden border border-nest-line flex-shrink-0 bg-nest-alt hover:ring-2 hover:ring-nest-clay transition cursor-pointer"
                                                            title="View staff photo"
                                                        >
                                                            <img
                                                                src={getVisitorPhotoSrc(log)}
                                                                alt=""
                                                                className="h-full w-full object-cover"
                                                            />
                                                        </button>
                                                    ) : (
                                                        <div className="h-8 w-8 rounded-xl border border-nest-line bg-nest-alt/70 flex-shrink-0 grid place-items-center text-nest-ink3">
                                                            <User size={14} />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="font-semibold text-nest-ink text-sm">
                                                            {log.name}
                                                        </p>
                                                        <div className="flex items-center gap-1.5 text-[11px] text-nest-ink3">
                                                            <span className="font-mono text-nest-clay font-medium">
                                                                {log.pass_code || "STAFF"}
                                                            </span>
                                                            {log.phone && <span>· {log.phone}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Role */}
                                            <td className="py-3 px-3 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-lg bg-nest-alt text-nest-ink2 border border-nest-line/80">
                                                    <RoleIcon size={12} className="text-nest-clay" />
                                                    <span>{log.service_type || "Staff"}</span>
                                                </span>
                                            </td>

                                            {/* Flats Served */}
                                            <td className="py-3 px-3 text-nest-ink2 max-w-[140px] truncate">
                                                {Array.isArray(log.flats) && log.flats.length > 0
                                                    ? log.flats.join(", ")
                                                    : "Society-wide"}
                                            </td>

                                            {/* Date */}
                                            <td className="py-3 px-3 whitespace-nowrap font-medium text-nest-ink">
                                                {log.date || formatDate(log.entered_at)}
                                            </td>

                                            {/* Entry (IN) */}
                                            <td className="py-3 px-3 whitespace-nowrap font-mono font-semibold text-emerald-800">
                                                {formatTime(log.entered_at)}
                                            </td>

                                            {/* Exit (OUT) */}
                                            <td className="py-3 px-3 whitespace-nowrap font-mono">
                                                {log.exited_at ? (
                                                    <span className="text-nest-ink2">{formatTime(log.exited_at)}</span>
                                                ) : (
                                                    <span className="text-emerald-700 font-semibold inline-flex items-center gap-1">
                                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                        Inside
                                                    </span>
                                                )}
                                            </td>

                                            {/* Duration */}
                                            <td className="py-3 px-3 whitespace-nowrap text-nest-ink font-medium">
                                                {log.duration_str || "—"}
                                            </td>

                                            {/* Vehicle */}
                                            <td className="py-3 px-3 whitespace-nowrap font-mono text-[11px] text-nest-ink2">
                                                {log.vehicle_no ? (
                                                    <span className="inline-flex items-center gap-1 bg-nest-alt px-1.5 py-0.5 rounded border border-nest-line">
                                                        {log.vehicle_type === "bike" ? (
                                                            <Bike size={11} className="text-nest-clay" />
                                                        ) : (
                                                            <Car size={11} className="text-nest-clay" />
                                                        )}
                                                        <span>{log.vehicle_no}</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-nest-ink3">—</span>
                                                )}
                                            </td>

                                            {/* Status Badge */}
                                            <td className="py-3 px-4 text-right whitespace-nowrap">
                                                <span
                                                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                                        isInside
                                                            ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                                            : "bg-nest-alt text-nest-ink2 border border-nest-line"
                                                    }`}
                                                >
                                                    {isInside ? "Inside Now" : "Exited"}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer bar */}
                <div className="p-3.5 px-6 border-t border-nest-line bg-nest-alt/40 flex items-center justify-between text-xs text-nest-ink3">
                    <span>
                        Showing {filteredLogs.length} attendance records
                        {startDate && endDate ? ` between ${startDate} and ${endDate}` : ""}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={exportToCSV}
                            disabled={filteredLogs.length === 0}
                            className="text-nest-clay hover:underline font-semibold cursor-pointer"
                        >
                            Download Spreadsheet (.CSV)
                        </button>
                        <span>·</span>
                        <button
                            type="button"
                            onClick={onClose}
                            className="hover:text-nest-ink cursor-pointer font-medium"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
